import { useState } from "react";
import { api } from "../../lib/api";
import { logWarn } from "../../lib/logger";
import { useData } from "../../contexts/DataContext";
import { toast } from "sonner";
import { CalendarRange, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../ui/dialog";
import { Switch } from "../ui/switch";
import { Button } from "../ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { cn } from "../../lib/utils";

function mondayOf(dateStr) {
  const d = dateStr ? new Date(`${dateStr}T00:00:00`) : new Date();
  const day = (d.getDay() + 6) % 7;
  d.setDate(d.getDate() - day);
  return d.toISOString().slice(0, 10);
}

async function blobError(err) {
  if (err?.response?.data instanceof Blob) {
    try {
      const parsed = JSON.parse(await err.response.data.text());
      if (typeof parsed?.detail === "string") return parsed.detail;
    } catch (parseErr) {
      logWarn("Could not parse weekly report error:", parseErr);
    }
  }
  return "Report generation failed. Please try again.";
}

export default function WeeklyReportDialog({ open, onClose }) {
  const { projects } = useData();
  const [weekStart, setWeekStart] = useState(mondayOf());
  const [projectId, setProjectId] = useState("all");
  const [format, setFormat] = useState("pdf");
  const [includeImages, setIncludeImages] = useState(true);
  const [generating, setGenerating] = useState(false);

  const generate = async () => {
    setGenerating(true);
    try {
      const res = await api.post("/reports/weekly", {
        week_start: weekStart,
        project_id: projectId === "all" ? null : projectId,
        format,
        include_images: includeImages,
      }, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: res.headers["content-type"] }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `bwn-weekly-${weekStart}.${format === "pdf" ? "pdf" : "xlsx"}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Weekly report (${format.toUpperCase()}) downloaded`);
      onClose();
    } catch (err) {
      toast.error(await blobError(err));
    } finally {
      setGenerating(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md bg-background" data-testid="weekly-report-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2"><CalendarRange size={17} className="text-[#1B2A6B] dark:text-blue-300" />Weekly Report</DialogTitle>
          <DialogDescription>Compile one week of project activity — tasks, subtasks, progress, checklists, notes and images.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-1">
          <div>
            <label className="text-xs font-medium text-muted-foreground">Week (any day — snaps to Monday)</label>
            <input type="date" data-testid="weekly-date-input" value={weekStart}
              onChange={(e) => setWeekStart(mondayOf(e.target.value))}
              className="mt-1 w-full h-9 px-3 text-sm rounded-md border border-border bg-transparent focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary" />
            <p className="mt-1 text-[11px] text-muted-foreground" data-testid="weekly-range-preview">
              {weekStart} → {(() => { const d = new Date(`${weekStart}T00:00:00`); d.setDate(d.getDate() + 6); return d.toISOString().slice(0, 10); })()}
            </p>
          </div>
          <div>
            <label className="text-xs font-medium text-muted-foreground">Project</label>
            <Select value={projectId} onValueChange={setProjectId}>
              <SelectTrigger data-testid="weekly-project-select" className="mt-1 h-9 text-sm"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All projects</SelectItem>
                {projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs font-medium text-muted-foreground">Format</label>
            <div className="mt-1 grid grid-cols-2 gap-2">
              {["pdf", "xlsx"].map((f) => (
                <button key={f} type="button" data-testid={`weekly-format-${f}`} onClick={() => setFormat(f)}
                  className={cn("h-9 rounded-md border text-sm font-medium uppercase transition-colors",
                    format === f ? "border-[#E31E24] bg-[#E31E24]/10 text-[#E31E24]" : "border-border text-muted-foreground hover:bg-muted")}>
                  {f}
                </button>
              ))}
            </div>
          </div>
          {format === "pdf" && (
            <div className="flex items-center justify-between rounded-md border border-border px-3 py-2.5">
              <div>
                <p className="text-sm font-medium">Include supporting images</p>
                <p className="text-xs text-muted-foreground">Embed photos from task chats &amp; attachments</p>
              </div>
              <Switch checked={includeImages} onCheckedChange={setIncludeImages} data-testid="weekly-include-images-toggle" />
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="weekly-cancel-button">Cancel</Button>
          <Button onClick={generate} disabled={generating} className="bg-[#E31E24] hover:bg-[#c41920] text-white" data-testid="weekly-generate-button">
            {generating ? <Loader2 size={15} className="mr-2 animate-spin" /> : <CalendarRange size={15} className="mr-2" />}
            {generating ? "Generating…" : "Generate"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
